import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ButtonBase, Collapse, IconButton, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { DebtType } from "../../Models/Credit";
import { INK } from "../../Utils/designTokens";
import { getRemoteText } from "../../Utils/firebase";
import { FormatAmountSymbol } from "../FormatData/FormatAmount";
import FormatDateToTR from "../FormatData/FormatDate";

interface Props {
  count: number;
  totalAmount: number;
  /** Pay is only enabled when the selection maps to a supported checkout flow. */
  payEnabled: boolean;
  onClear: () => void;
  onPay: () => void;
  /** Snooze of the selected payments via the bulk snooze flow (design `bulk-defer`). */
  snoozeEnabled: boolean;
  /** True when only part of the selection is snoozable — label switches. */
  snoozePartial: boolean;
  onSnooze: () => void;
  /** Selected payments, shown in the expandable review sheet (design `bulk-list`). */
  items: DebtType[];
  /** Removes one payment from the selection (sheet row X button). */
  onRemove: (debt: DebtType) => void;
}

/**
 * CRL-3241 (home-payment-multiselect): floating action bar shown while
 * pending payments are selected. The chevron expands a review sheet listing
 * the selected payments (new-design.html `bulkbar`). `onPay` is the single
 * seam to wire a real bulk payment endpoint to when the backend provides one.
 */
const PaymentsSelectionBar = ({
  count,
  totalAmount,
  payEnabled,
  onClear,
  onPay,
  snoozeEnabled,
  snoozePartial,
  onSnooze,
  items,
  onRemove,
}: Props) => {
  const [expanded, setExpanded] = useState(false);

  // Collapse when the selection empties so a fresh selection starts closed.
  useEffect(() => {
    if (count === 0) setExpanded(false);
  }, [count]);

  if (count === 0) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: { xs: "12px", md: "24px" },
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1200,
        width: { xs: "calc(100% - 24px)", md: "auto" },
        minWidth: { md: "560px" },
        maxWidth: "660px",
      }}
    >
      <Stack
        sx={{
          backgroundColor: INK,
          color: "#FFF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0px 12px 32px rgba(25, 27, 30, 0.35)",
        }}
      >
        {/* Review sheet — selected payments */}
        <Collapse in={expanded} timeout={280}>
          <Box
            sx={{
              maxHeight: "262px",
              overflowY: "auto",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            {items.map((debt, idx) => (
              <Stack
                key={debt.id}
                direction="row"
                alignItems="center"
                gap="10px"
                sx={{
                  padding: "11px 16px",
                  borderTop: idx === 0 ? "none" : "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <img
                  src={debt.shop.logoUrl}
                  alt={debt.shop.name}
                  style={{
                    width: "32px",
                    height: "32px",
                    borderRadius: "9px",
                    objectFit: "cover",
                    flexShrink: 0,
                  }}
                />
                <Stack sx={{ flex: 1, minWidth: 0, lineHeight: 1.25 }}>
                  <span className="text-[13.5px] font-bold truncate">
                    {debt.shop.name}
                  </span>
                  <span
                    className="text-[11.5px]"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {getRemoteText("last_payment_prefix", "Son ödeme")}{" "}
                    {FormatDateToTR(debt.dueDate)}
                  </span>
                </Stack>
                <span className="text-[13.5px] font-bold whitespace-nowrap">
                  {FormatAmountSymbol(debt.remainingPaymentAmount)}
                </span>
                <IconButton
                  aria-label={getRemoteText("remove_from_selection", "Çıkar")}
                  onClick={() => onRemove(debt)}
                  sx={{
                    width: "26px",
                    height: "26px",
                    borderRadius: "7px",
                    flexShrink: 0,
                    color: "rgba(255,255,255,0.6)",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.14)",
                      color: "#FFF",
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Stack>
            ))}
          </Box>
        </Collapse>

        {/* Main row */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
          gap={{ xs: "10px", md: "24px" }}
          sx={{ padding: { xs: "12px 14px", sm: "10px 12px 10px 14px" } }}
        >
          <ButtonBase
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: { xs: "center", sm: "flex-start" },
              gap: "8px",
              padding: "6px",
              borderRadius: "10px",
              fontSize: "14px",
              fontWeight: 600,
              whiteSpace: "nowrap",
              "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                color: "rgba(255,255,255,0.7)",
                transition: "transform 220ms ease",
                transform: expanded ? "rotate(180deg)" : "none",
              }}
            />
            {getRemoteText("selected_payments_x", "{val} ödeme seçili", count.toString())}
          </ButtonBase>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={{ xs: "space-between", sm: "flex-end" }}
            gap={{ xs: "8px", md: "16px" }}
          >
            <ButtonBase
              onClick={onClear}
              sx={{
                color: "#E2E8F0",
                fontSize: "14px",
                fontWeight: 600,
                padding: "8px 10px",
                borderRadius: "10px",
                "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
              }}
            >
              {getRemoteText("clear_selection", "Temizle")}
            </ButtonBase>
            <ButtonBase
              onClick={onSnooze}
              disabled={!snoozeEnabled}
              sx={{
                border: "1px solid rgba(255,255,255,0.35)",
                color: snoozeEnabled ? "#FFF" : "rgba(255,255,255,0.45)",
                fontSize: "14px",
                fontWeight: 700,
                padding: "9px 16px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: snoozeEnabled
                    ? "rgba(255,255,255,0.1)"
                    : "transparent",
                },
                "&.Mui-disabled": {
                  border: "1px solid rgba(255,255,255,0.18)",
                },
              }}
            >
              {snoozePartial
                ? getRemoteText("snooze_eligible_selection", "Uygun olanları ertele")
                : getRemoteText("snooze", "Ertele")}
            </ButtonBase>
            <ButtonBase
              onClick={onPay}
              disabled={!payEnabled}
              sx={{
                backgroundColor: payEnabled ? "#FFF" : "rgba(255,255,255,0.25)",
                color: payEnabled ? INK : "rgba(255,255,255,0.6)",
                fontSize: "14px",
                fontWeight: 700,
                padding: "10px 18px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                flexGrow: { xs: 1, sm: 0 },
                transition: "background-color 120ms ease",
                "&:hover": {
                  backgroundColor: payEnabled ? "#F1F5F9" : "rgba(255,255,255,0.25)",
                },
              }}
            >
              {getRemoteText(
                "pay_amount_x",
                "{val} öde",
                FormatAmountSymbol(totalAmount)
              )}
            </ButtonBase>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PaymentsSelectionBar;
